import React, { type Dispatch, type FunctionComponent, type SetStateAction, useState } from 'react'
import { createCSVFileForDownload, generateCSV } from '../../lib/csv'
import { Dialog } from './Dialog'
import { format } from 'date-fns'
import { readableCharityFieldMap } from '../../lib/charity'
import { readableCampaignFieldMap } from '../../lib/campaign'
import { RecursiveList } from '../pages/charities/charity-report/common/preview/RecursiveList'
import { getValueForKey } from '../../lib/objectUtils'
import { logError } from '@tomra/datadog-browser-logging'
import { Button } from '@tomra/design-system-react/button'
import { Checkbox } from '@tomra/design-system-react/checkbox'
import { Label } from '@tomra/design-system-react/label'

const availableProperties = {
  charity: readableCharityFieldMap,
  campaign: readableCampaignFieldMap
}

const CheckListElement: FunctionComponent<{
  item: string
  keyMap: object
  selectedItems: string[]
  setSelectedItems: Dispatch<SetStateAction<string[]>>
}> = ({ item, keyMap, selectedItems, setSelectedItems }) => {
  return (
    <div key={item}>
      <div className="flex items-center gap-sm">
        <Checkbox
          id={item}
          checked={selectedItems.includes(item)}
          onCheckedChange={() => {
            setSelectedItems(prevState => {
              if (prevState.includes(item)) {
                return prevState.filter(p => p !== item)
              }
              return [...prevState, item]
            })
          }}
        />
        <Label htmlFor={item} className="cursor-pointer text-midnight">
          {getValueForKey(keyMap, item)}
        </Label>
      </div>
    </div>
  )
}

type Props = {
  filePrefix: string
  data: CharityReportType[]
}

export const GenerateCSVReport: FunctionComponent<Props> = ({ filePrefix, data }) => {
  const [reportStatus, setReportStatus] = useState<'idle' | 'pending' | 'success' | 'failure'>('idle')
  const [showSelectProperties, setShowSelectProperties] = useState(false)
  const [selectedProperties, setSelectedProperties] = useState<string[]>([])

  function selectProperties() {
    setShowSelectProperties(true)
  }

  function downloadReport(url: string, filename: string) {
    try {
      const link = document.createElement('a')
      link.href = url as string
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url as string)

      setReportStatus('success')
      setShowSelectProperties(false)
    } catch (_error) {
      setReportStatus('failure')
    } finally {
      setTimeout(() => {
        setReportStatus('idle')
      }, 4000)
    }
  }

  function generateReport() {
    try {
      if (selectedProperties.length === 0) {
        return
      }

      setReportStatus('pending')
      const filename = `${filePrefix}-report-${format(new Date(), 'yyyy-MM-dd')}.csv`
      const csv = generateCSV(data, selectedProperties)
      const url = createCSVFileForDownload(csv)
      downloadReport(url as string, filename)
    } catch (error) {
      logError(new Error('Failed to generate Charity CSV report'), error as Error)
      setReportStatus('failure')
    }
  }

  return (
    <>
      {reportStatus === 'success' && (
        <div className="fixed z-[2] top-lg right-lg alert alert-success">
          Report was generated and is downloaded to your computer
        </div>
      )}

      {reportStatus === 'failure' && (
        <div className="fixed z-[2] top-lg right-lg alert alert-danger mb-sm">Something went wrong</div>
      )}

      <Button onClick={selectProperties}>Export CSV</Button>

      {showSelectProperties && (
        <Dialog onClose={() => setShowSelectProperties(false)} ariaLabel="Select report properties">
          <div className="min-w-screen-md mx-auto card p-lg">
            <h1 className="text-xl pb-sm">Select properties</h1>
            <p>Choose the properties you want to include in the report.</p>

            <div className="max-h-[60vh] overflow-scroll mt-md grid grid-cols-2 gap-md items-start">
              <div role="group" aria-label="Charity">
                <RecursiveList
                  prefix="charity"
                  data={availableProperties.charity}
                  keyMap={availableProperties}
                  keysToIgnore={['updatedAt', 'updatedBy', 'administratorUserId', 'logoUrl', 'registeredState']}
                  checklistOptions={{
                    CheckListElement,
                    selectedItems: selectedProperties,
                    setSelectedItems: setSelectedProperties
                  }}
                />
              </div>

              <div role="group" aria-label="Campaign">
                <RecursiveList
                  prefix="campaign"
                  data={availableProperties.campaign}
                  keyMap={availableProperties}
                  keysToIgnore={[
                    'updatedAt',
                    'updatedBy',
                    'logoUrl',
                    'charityId',
                    'facebookHandle',
                    'instagramHandle',
                    'linkedinHandle',
                    'snapchatHandle',
                    'tiktokHandle',
                    'youtubeHandle'
                  ]}
                  checklistOptions={{
                    CheckListElement,
                    selectedItems: selectedProperties,
                    setSelectedItems: setSelectedProperties
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Button variant="primary" onClick={generateReport} aria-disabled={selectedProperties.length === 0}>
                Download CSV
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  )
}
